import { useState, useEffect } from 'react'
import { supabase } from './lib/supabaseClient'
import * as XLSX from 'xlsx'
import WhatsAppButton from './components/WhatsAppButton'
import MessagingSystem from './components/MessagingSystem'
import PortalLayout from './components/PortalLayout'

function CROPortal({ session, onLogout }) {
  const [activeTab, setActiveTab] = useState('dashboard')
  const [companyId, setCompanyId] = useState(null)
  const [companyName, setCompanyName] = useState('')
  const [clients, setClients] = useState([])
  const [properties, setProperties] = useState([])
  const [staff, setStaff] = useState([])
  const [interactions, setInteractions] = useState([])
  const [complaints, setComplaints] = useState([])
  const [feedback, setFeedback] = useState([])
  const [appointments, setAppointments] = useState([])
  const [documents, setDocuments] = useState([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [showDocModal, setShowDocModal] = useState(false)
  const [showMessages, setShowMessages] = useState(false)
  const [unreadCount, setUnreadCount] = useState(0)
  const [editingItem, setEditingItem] = useState(null)
  const [formData, setFormData] = useState({})
  const [docFormData, setDocFormData] = useState({})
  const [docFile, setDocFile] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterStatus, setFilterStatus] = useState('all')
  const [errorMsg, setErrorMsg] = useState('')
  const [uploading, setUploading] = useState(false)
  const [stats, setStats] = useState({
    totalClients: 0,
    totalProperties: 0,
    activeComplaints: 0,
    pendingAppointments: 0,
    satisfactionRate: 0,
    totalStaff: 0,
    totalDocuments: 0
  })

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: '📊' },
    { id: 'clients', label: 'Clients', icon: '👥' },
    { id: 'properties', label: 'Properties', icon: '🏠' },
    { id: 'staff', label: 'Staff Management', icon: '👔' },
    { id: 'documents', label: 'Documents', icon: '📄' },
    { id: 'interactions', label: 'Interactions', icon: '💬' },
    { id: 'complaints', label: 'Complaints', icon: '⚠️' },
    { id: 'feedback', label: 'Feedback', icon: '⭐' },
    { id: 'appointments', label: 'Appointments', icon: '📅' },
    { id: 'reports', label: 'Reports', icon: '📊' },
    { id: 'whatsapp', label: 'WhatsApp', icon: '📱' }
  ]

  const complaintTypes = ['property', 'payment', 'service', 'communication', 'other']
  const interactionTypes = ['call', 'email', 'meeting', 'whatsapp', 'site_visit']
  const appointmentTypes = ['property_viewing', 'consultation', 'document_signing', 'follow_up']
  const documentTypes = ['contract', 'agreement', 'certificate', 'report', 'general']

  // ---------- Get company ----------
  useEffect(() => {
    const getCompanyId = async () => {
      if (!session?.user?.id) return

      const { data: userData, error } = await supabase
        .from('users')
        .select('company_id')
        .eq('id', session.user.id)
        .maybeSingle()

      if (error) {
        console.error('Error fetching company_id:', error)
        setLoading(false)
        return
      }

      if (!userData?.company_id) {
        console.log('No company_id found')
        setLoading(false)
        return
      }

      setCompanyId(userData.company_id)

      const { data: company } = await supabase
        .from('companies')
        .select('name')
        .eq('id', userData.company_id)
        .maybeSingle()

      if (company) setCompanyName(company.name)
      setLoading(false)
    }

    getCompanyId()
  }, [session])

  // ---------- Fetch on tab/company change ----------
  useEffect(() => {
    if (companyId) {
      fetchData()
      fetchUnreadMessages()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, companyId])

  const fetchUnreadMessages = async () => {
    if (!companyId || !session?.user?.id) return
    const { count } = await supabase
      .from('messages')
      .select('*', { count: 'exact', head: true })
      .neq('sender_id', session.user.id)
      .eq('is_read', false)
      .eq('company_id', companyId)

    setUnreadCount(count || 0)
  }

  const fetchData = async () => {
    if (!companyId) return
    setLoading(true)
    setErrorMsg('')

    try {
      const [
        { data: clientsData },
        { data: propertiesData },
        { data: staffData },
        { data: documentsData },
        { data: interactionsData },
        { data: complaintsData },
        { data: feedbackData },
        { data: appointmentsData }
      ] = await Promise.all([
        supabase.from('clients').select('*').eq('company_id', companyId).order('created_at', { ascending: false }),
        supabase.from('properties').select('*, clients(full_name)').eq('company_id', companyId).order('created_at', { ascending: false }),
        supabase.from('users').select('*').eq('company_id', companyId),
        supabase.from('documents').select('*').eq('company_id', companyId).order('created_at', { ascending: false }),
        supabase.from('client_interactions').select('*, clients(full_name)').eq('company_id', companyId).order('interaction_date', { ascending: false }),
        supabase.from('complaints').select('*, clients(full_name)').eq('company_id', companyId).order('created_at', { ascending: false }),
        supabase.from('feedback').select('*, clients(full_name)').eq('company_id', companyId).order('created_at', { ascending: false }),
        supabase.from('appointments').select('*, clients(full_name)').eq('company_id', companyId).order('appointment_date', { ascending: false })
      ])

      setClients(clientsData || [])
      setProperties((propertiesData || []).map(p => ({ ...p, client_name: p.clients?.full_name || 'N/A' })))
      setStaff(staffData || [])
      setDocuments(documentsData || [])
      setInteractions(interactionsData || [])
      setComplaints(complaintsData || [])
      setFeedback(feedbackData || [])
      setAppointments(appointmentsData || [])

      setStats({
        totalClients: clientsData?.length || 0,
        totalProperties: propertiesData?.length || 0,
        activeComplaints: complaintsData?.filter(c => ['open', 'in_progress'].includes(c.status)).length || 0,
        pendingAppointments: appointmentsData?.filter(a => a.status === 'scheduled').length || 0,
        satisfactionRate: calculateSatisfactionRate(feedbackData || []),
        totalStaff: staffData?.length || 0,
        totalDocuments: documentsData?.length || 0
      })
    } catch (err) {
      console.error('Fetch error:', err)
      setErrorMsg(err.message)
    }
    setLoading(false)
  }

  const calculateSatisfactionRate = (feedbackData) => {
    if (!feedbackData.length) return 0
    const avg = feedbackData.reduce((s, f) => s + (f.rating || 0), 0) / feedbackData.length
    return Math.round((avg / 5) * 100)
  }

  // ---------- Submit (add / edit) ----------
  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!companyId) return
    setLoading(true)

    try {
      let table = ''
      const data = { ...formData, company_id: companyId }
      delete data.clients // never push joined object back

      if (activeTab === 'clients') {
        table = 'clients'
      } else if (activeTab === 'properties') {
        table = 'properties'
        data.price = parseFloat(data.price) || 0
        data.bedrooms = parseInt(data.bedrooms) || 0
        data.bathrooms = parseInt(data.bathrooms) || 0
        data.size = parseInt(data.size) || 0
        // Normalise features to a clean array
        if (Array.isArray(data.features)) {
          data.features = data.features.map(f => String(f).trim()).filter(Boolean)
        } else if (typeof data.features === 'string') {
          data.features = data.features.split(',').map(f => f.trim()).filter(Boolean)
        }
      } else if (activeTab === 'staff') {
        table = 'users'
      } else if (activeTab === 'interactions') {
        table = 'client_interactions'
      } else if (activeTab === 'complaints') {
        table = 'complaints'
      } else if (activeTab === 'feedback') {
        table = 'feedback'
        data.rating = parseInt(data.rating) || 5
      } else if (activeTab === 'appointments') {
        table = 'appointments'
      }

      if (!table) throw new Error('Unknown tab')

      // Convert datetime-local to ISO for timestamps
      if (data.interaction_date) data.interaction_date = new Date(data.interaction_date).toISOString()
      if (data.follow_up_date) data.follow_up_date = new Date(data.follow_up_date).toISOString()
      if (data.appointment_date) data.appointment_date = new Date(data.appointment_date).toISOString()

      if (editingItem) {
        const { error } = await supabase.from(table).update(data).eq('id', editingItem.id)
        if (error) throw error
      } else {
        const { error } = await supabase.from(table).insert(data)
        if (error) throw error
      }

      setShowModal(false)
      setEditingItem(null)
      setFormData({})
      await fetchData()
    } catch (err) {
      console.error(err)
      alert('Error: ' + err.message)
    }
    setLoading(false)
  }

  const handleDelete = async (id, table) => {
    if (!confirm('Are you sure you want to delete this record?')) return
    const { error } = await supabase.from(table).delete().eq('id', id)
    if (error) return alert('Error: ' + error.message)
    await fetchData()
  }

  const updateStaffRole = async (userId, newRole) => {
    const { error } = await supabase.from('users').update({ role: newRole }).eq('id', userId)
    if (error) return alert('Error: ' + error.message)
    await fetchData()
  }

  // ---------- Document upload ----------
  const handleDocumentUpload = async (e) => {
    e.preventDefault()
    if (!docFile) return alert('Please select a file')

    const allowedTypes = [
      'image/png', 'image/jpeg', 'image/jpg', 'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ]
    if (!allowedTypes.includes(docFile.type)) {
      return alert('File type not supported. Upload PDF, DOC, DOCX, PNG, or JPEG.')
    }
    if (docFile.size > 10 * 1024 * 1024) {
      return alert('File too large. Maximum size is 10MB.')
    }

    setUploading(true)
    try {
      const fileExt = docFile.name.split('.').pop()
      const fileName = `cro_doc_${companyId}_${Date.now()}.${fileExt}`

      const { error: uploadError } = await supabase.storage
        .from('property-images')
        .upload(fileName, docFile, { cacheControl: '3600', upsert: true })

      if (uploadError) throw uploadError

      const { data: { publicUrl } } = supabase.storage
        .from('property-images')
        .getPublicUrl(fileName)

      const { error: dbError } = await supabase
        .from('documents')
        .insert({
          title: docFormData.title || docFile.name,
          file_url: publicUrl,
          type: docFormData.type || 'general',
          version: docFormData.version || '1.0',
          description: docFormData.description || '',
          client_id: docFormData.client_id || null,
          uploaded_by: session.user.id,
          company_id: companyId
        })

      if (dbError) {
        await supabase.storage.from('property-images').remove([fileName])
        throw dbError
      }

      setShowDocModal(false)
      setDocFormData({})
      setDocFile(null)
      await fetchData()
    } catch (err) {
      console.error(err)
      alert('Upload failed: ' + err.message)
    }
    setUploading(false)
  }

  const deleteDocument = async (docId) => {
    if (!confirm('Delete this document?')) return

    const { data: doc } = await supabase
      .from('documents')
      .select('file_url')
      .eq('id', docId)
      .single()

    if (doc?.file_url) {
      const fileName = doc.file_url.split('/').pop()
      await supabase.storage.from('property-images').remove([fileName])
    }

    const { error } = await supabase.from('documents').delete().eq('id', docId)
    if (error) return alert('Error: ' + error.message)
    await fetchData()
  }

  // ---------- Utilities ----------
  const exportToExcel = (data, filename) => {
    if (!data.length) return alert('No data to export')
    const ws = XLSX.utils.json_to_sheet(data)
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, filename)
    XLSX.writeFile(wb, `${companyName}_${filename}_${new Date().toISOString().split('T')[0]}.xlsx`)
  }

  const getPriorityColor = (priority) => ({
    low: '#10b981', medium: '#f59e0b', high: '#f97316', urgent: '#dc2626'
  }[priority] || '#6b7280')

  const getStatusBadge = (status) => ({
    scheduled: { bg: '#dbeafe', color: '#1e40af', label: '📅 Scheduled' },
    completed: { bg: '#d1fae5', color: '#059669', label: '✅ Completed' },
    cancelled: { bg: '#fee2e2', color: '#dc2626', label: '❌ Cancelled' },
    rescheduled: { bg: '#fef3c7', color: '#d97706', label: '🔄 Rescheduled' },
    open: { bg: '#fee2e2', color: '#dc2626', label: '🟡 Open' },
    in_progress: { bg: '#fef3c7', color: '#d97706', label: '⏳ In Progress' },
    resolved: { bg: '#d1fae5', color: '#059669', label: '✅ Resolved' },
    closed: { bg: '#e2e8f0', color: '#64748b', label: '🔒 Closed' }
  }[status] || { bg: '#e2e8f0', color: '#64748b', label: status })

  const filteredClients = clients.filter(c =>
    c.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.phone?.includes(searchTerm)
  )

  // ---------- Form renderer ----------
  const renderForm = () => {
    const inputStyle = { width: '100%', padding: '10px', margin: '6px 0', border: '1px solid #cbd5e1', borderRadius: '8px', boxSizing: 'border-box' }

    if (activeTab === 'staff') {
      return (
        <>
          <h3 style={{ margin: '10px 0 5px 0', fontSize: '16px', color: '#1e3a5f' }}>👤 Personal Information</h3>
          <input placeholder="First Name" value={formData.first_name || ''} onChange={e => setFormData({ ...formData, first_name: e.target.value })} style={inputStyle} />
          <input placeholder="Last Name" value={formData.last_name || ''} onChange={e => setFormData({ ...formData, last_name: e.target.value })} style={inputStyle} />
          <input placeholder="Email *" type="email" value={formData.email || ''} onChange={e => setFormData({ ...formData, email: e.target.value })} required style={inputStyle} />
          <input placeholder="Phone Number" value={formData.phone || ''} onChange={e => setFormData({ ...formData, phone: e.target.value })} style={inputStyle} />
          <input placeholder="Date of Birth" type="date" value={formData.date_of_birth || ''} onChange={e => setFormData({ ...formData, date_of_birth: e.target.value })} style={inputStyle} />

          <h3 style={{ margin: '15px 0 5px 0', fontSize: '16px', color: '#1e3a5f' }}>🏢 Employment Details</h3>
          <select value={formData.role || 'agent'} onChange={e => setFormData({ ...formData, role: e.target.value })} style={inputStyle}>
            {['agent', 'cro', 'accounting', 'marketing', 'legal', 'recruitment', 'leadership'].map(r => <option key={r} value={r}>{r}</option>)}
          </select>
          <input placeholder="Position / Title" value={formData.position || ''} onChange={e => setFormData({ ...formData, position: e.target.value })} style={inputStyle} />
          <input placeholder="Department" value={formData.department || ''} onChange={e => setFormData({ ...formData, department: e.target.value })} style={inputStyle} />
          <input placeholder="Hire Date" type="date" value={formData.hire_date || ''} onChange={e => setFormData({ ...formData, hire_date: e.target.value })} style={inputStyle} />

          <h3 style={{ margin: '15px 0 5px 0', fontSize: '16px', color: '#1e3a5f' }}>🎓 Qualifications & Bio</h3>
          <textarea placeholder="Qualifications / Certifications" rows="3" value={formData.qualification || ''} onChange={e => setFormData({ ...formData, qualification: e.target.value })} style={inputStyle} />
          <textarea placeholder="Bio / Professional Background" rows="3" value={formData.bio || ''} onChange={e => setFormData({ ...formData, bio: e.target.value })} style={inputStyle} />

          <h3 style={{ margin: '15px 0 5px 0', fontSize: '16px', color: '#1e3a5f' }}>📍 Address & Emergency</h3>
          <input placeholder="Residential Address" value={formData.address || ''} onChange={e => setFormData({ ...formData, address: e.target.value })} style={inputStyle} />
          <input placeholder="Emergency Contact" value={formData.emergency_contact || ''} onChange={e => setFormData({ ...formData, emergency_contact: e.target.value })} style={inputStyle} />

          <div style={{ marginTop: '10px', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <label><input type="checkbox" checked={formData.is_active !== false} onChange={e => setFormData({ ...formData, is_active: e.target.checked })} /> Active Employee</label>
            <label><input type="checkbox" checked={formData.onboarding_completed || false} onChange={e => setFormData({ ...formData, onboarding_completed: e.target.checked })} /> Onboarding Completed</label>
          </div>
        </>
      )
    }

    if (activeTab === 'clients') {
      return (
        <>
          <input placeholder="Full Name *" value={formData.full_name || ''} onChange={e => setFormData({ ...formData, full_name: e.target.value })} required style={inputStyle} />
          <input placeholder="Email *" type="email" value={formData.email || ''} onChange={e => setFormData({ ...formData, email: e.target.value })} required style={inputStyle} />
          <input placeholder="Phone" value={formData.phone || ''} onChange={e => setFormData({ ...formData, phone: e.target.value })} style={inputStyle} />
          <input placeholder="Address" value={formData.address || ''} onChange={e => setFormData({ ...formData, address: e.target.value })} style={inputStyle} />
          <select value={formData.client_type || 'buyer'} onChange={e => setFormData({ ...formData, client_type: e.target.value })} style={inputStyle}>
            {['buyer', 'seller', 'renter', 'investor'].map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <textarea placeholder="Bio / Notes" rows="3" value={formData.bio || ''} onChange={e => setFormData({ ...formData, bio: e.target.value })} style={inputStyle} />
          <input placeholder="Preferred Contact Method" value={formData.preferred_contact || ''} onChange={e => setFormData({ ...formData, preferred_contact: e.target.value })} style={inputStyle} />
          <textarea placeholder="Additional Notes" rows="2" value={formData.notes || ''} onChange={e => setFormData({ ...formData, notes: e.target.value })} style={inputStyle} />
        </>
      )
    }

    if (activeTab === 'properties') {
      const featuresValue = Array.isArray(formData.features)
        ? formData.features.join(', ')
        : (formData.features || '')
      return (
        <>
          <select value={formData.client_id || ''} onChange={e => setFormData({ ...formData, client_id: e.target.value })} style={inputStyle}>
            <option value="">Select Client</option>
            {clients.map(c => <option key={c.id} value={c.id}>{c.full_name}</option>)}
          </select>
          <input placeholder="Property Address *" value={formData.address || ''} onChange={e => setFormData({ ...formData, address: e.target.value })} required style={inputStyle} />
          <select value={formData.property_type || ''} onChange={e => setFormData({ ...formData, property_type: e.target.value })} style={inputStyle}>
            <option value="">Select Type</option>
            {['house', 'apartment', 'land', 'commercial'].map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <input placeholder="Price" type="number" value={formData.price || ''} onChange={e => setFormData({ ...formData, price: e.target.value })} style={inputStyle} />
          <input placeholder="Bedrooms" type="number" value={formData.bedrooms || ''} onChange={e => setFormData({ ...formData, bedrooms: e.target.value })} style={inputStyle} />
          <input placeholder="Bathrooms" type="number" value={formData.bathrooms || ''} onChange={e => setFormData({ ...formData, bathrooms: e.target.value })} style={inputStyle} />
          <input placeholder="Size (sq ft)" type="number" value={formData.size || ''} onChange={e => setFormData({ ...formData, size: e.target.value })} style={inputStyle} />
          <textarea placeholder="Description" rows="3" value={formData.description || ''} onChange={e => setFormData({ ...formData, description: e.target.value })} style={inputStyle} />
          <input placeholder="Features (comma separated)" value={featuresValue} onChange={e => setFormData({ ...formData, features: e.target.value })} style={inputStyle} />
          <select value={formData.status || 'available'} onChange={e => setFormData({ ...formData, status: e.target.value })} style={inputStyle}>
            {['available', 'sold', 'rented', 'pending'].map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </>
      )
    }

    if (activeTab === 'interactions') {
      return (
        <>
          <select value={formData.client_id || ''} onChange={e => setFormData({ ...formData, client_id: e.target.value })} required style={inputStyle}>
            <option value="">Select Client</option>
            {clients.map(c => <option key={c.id} value={c.id}>{c.full_name}</option>)}
          </select>
          <select value={formData.interaction_type || ''} onChange={e => setFormData({ ...formData, interaction_type: e.target.value })} required style={inputStyle}>
            <option value="">Select Type</option>
            {interactionTypes.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <input type="datetime-local" value={formData.interaction_date || ''} onChange={e => setFormData({ ...formData, interaction_date: e.target.value })} required style={inputStyle} />
          <textarea placeholder="Notes" rows="4" value={formData.notes || ''} onChange={e => setFormData({ ...formData, notes: e.target.value })} required style={inputStyle} />
          <select value={formData.follow_up_needed || 'no'} onChange={e => setFormData({ ...formData, follow_up_needed: e.target.value })} style={inputStyle}>
            <option value="no">No Follow-up Needed</option>
            <option value="yes">Follow-up Needed</option>
          </select>
          {formData.follow_up_needed === 'yes' && (
            <input type="datetime-local" value={formData.follow_up_date || ''} onChange={e => setFormData({ ...formData, follow_up_date: e.target.value })} style={inputStyle} />
          )}
        </>
      )
    }

    if (activeTab === 'complaints') {
      return (
        <>
          <select value={formData.client_id || ''} onChange={e => setFormData({ ...formData, client_id: e.target.value })} required style={inputStyle}>
            <option value="">Select Client</option>
            {clients.map(c => <option key={c.id} value={c.id}>{c.full_name}</option>)}
          </select>
          <select value={formData.complaint_type || ''} onChange={e => setFormData({ ...formData, complaint_type: e.target.value })} required style={inputStyle}>
            <option value="">Select Type</option>
            {complaintTypes.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <textarea placeholder="Description" rows="4" value={formData.description || ''} onChange={e => setFormData({ ...formData, description: e.target.value })} required style={inputStyle} />
          <select value={formData.status || 'open'} onChange={e => setFormData({ ...formData, status: e.target.value })} style={inputStyle}>
            {['open', 'in_progress', 'resolved', 'closed'].map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={formData.priority || 'medium'} onChange={e => setFormData({ ...formData, priority: e.target.value })} style={inputStyle}>
            {['low', 'medium', 'high', 'urgent'].map(p => <option key={p} value={p}>{p}</option>)}
          </select>
          <textarea placeholder="Resolution Notes" rows="3" value={formData.resolution_notes || ''} onChange={e => setFormData({ ...formData, resolution_notes: e.target.value })} style={inputStyle} />
        </>
      )
    }

    if (activeTab === 'feedback') {
      return (
        <>
          <select value={formData.client_id || ''} onChange={e => setFormData({ ...formData, client_id: e.target.value })} required style={inputStyle}>
            <option value="">Select Client</option>
            {clients.map(c => <option key={c.id} value={c.id}>{c.full_name}</option>)}
          </select>
          <input type="number" min="1" max="5" placeholder="Rating (1-5)" value={formData.rating || ''} onChange={e => setFormData({ ...formData, rating: e.target.value })} required style={inputStyle} />
          <textarea placeholder="Feedback" rows="4" value={formData.feedback || ''} onChange={e => setFormData({ ...formData, feedback: e.target.value })} required style={inputStyle} />
          <select value={formData.category || ''} onChange={e => setFormData({ ...formData, category: e.target.value })} style={inputStyle}>
            <option value="">Select Category</option>
            {['service', 'property', 'communication', 'overall'].map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </>
      )
    }

    if (activeTab === 'appointments') {
      return (
        <>
          <select value={formData.client_id || ''} onChange={e => setFormData({ ...formData, client_id: e.target.value })} required style={inputStyle}>
            <option value="">Select Client</option>
            {clients.map(c => <option key={c.id} value={c.id}>{c.full_name}</option>)}
          </select>
          <input type="datetime-local" value={formData.appointment_date || ''} onChange={e => setFormData({ ...formData, appointment_date: e.target.value })} required style={inputStyle} />
          <select value={formData.appointment_type || ''} onChange={e => setFormData({ ...formData, appointment_type: e.target.value })} required style={inputStyle}>
            <option value="">Select Type</option>
            {appointmentTypes.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <input placeholder="Location" value={formData.location || ''} onChange={e => setFormData({ ...formData, location: e.target.value })} style={inputStyle} />
          <input placeholder="Meeting Link (if virtual)" value={formData.meeting_link || ''} onChange={e => setFormData({ ...formData, meeting_link: e.target.value })} style={inputStyle} />
          <textarea placeholder="Notes" rows="3" value={formData.notes || ''} onChange={e => setFormData({ ...formData, notes: e.target.value })} style={inputStyle} />
          <select value={formData.status || 'scheduled'} onChange={e => setFormData({ ...formData, status: e.target.value })} style={inputStyle}>
            {['scheduled', 'completed', 'cancelled', 'rescheduled'].map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </>
      )
    }

    return null
  }

  // ---------- Staff list ----------
  const renderStaffList = () => {
    const cardStyle = { background: '#fff', borderRadius: '12px', padding: '20px', border: '1px solid #e2e8f0' }
    const buttonStyle = { background: '#2c7da0', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: '6px', cursor: 'pointer', marginRight: '8px' }
    const deleteButtonStyle = { ...buttonStyle, background: '#dc2626' }

    return (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: '20px' }}>
        {staff.map(member => (
          <div key={member.id} style={cardStyle}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
              <div style={{ width: '50px', height: '50px', borderRadius: '50%', background: '#1e3a5f', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px', fontWeight: 'bold' }}>
                {member.first_name?.charAt(0) || member.email?.charAt(0) || '👤'}
              </div>
              <div>
                <h3 style={{ margin: 0 }}>{member.first_name || ''} {member.last_name || ''}</h3>
                <p style={{ margin: 0, fontSize: '13px', color: '#64748b' }}>{member.email}</p>
              </div>
              <span style={{ marginLeft: 'auto', padding: '4px 12px', borderRadius: '12px', fontSize: '12px', background: '#d1fae5', color: '#059669' }}>
                {member.role || 'agent'}
              </span>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 12px', fontSize: '14px' }}>
              <p><strong>📞 Phone:</strong> {member.phone || 'N/A'}</p>
              <p><strong>🏢 Dept:</strong> {member.department || 'N/A'}</p>
              <p><strong>📌 Position:</strong> {member.position || 'N/A'}</p>
              <p><strong>🎓 Qualification:</strong> {member.qualification || 'N/A'}</p>
              <p><strong>📍 Address:</strong> {member.address || 'N/A'}</p>
              <p><strong>📅 Hire Date:</strong> {member.hire_date ? new Date(member.hire_date).toLocaleDateString() : 'N/A'}</p>
              <p><strong>🎂 DOB:</strong> {member.date_of_birth ? new Date(member.date_of_birth).toLocaleDateString() : 'N/A'}</p>
              <p><strong>🚨 Emergency:</strong> {member.emergency_contact || 'N/A'}</p>
              <p><strong>✅ Status:</strong> {member.is_active !== false ? 'Active' : 'Inactive'}</p>
              <p><strong>📋 Onboarding:</strong> {member.onboarding_completed ? '✅ Complete' : '⏳ Pending'}</p>
            </div>

            {member.bio && (
              <p style={{ marginTop: '8px', fontSize: '13px', color: '#475569', borderTop: '1px solid #e2e8f0', paddingTop: '8px' }}>
                📝 {member.bio.substring(0, 100)}...
              </p>
            )}

            <div style={{ marginTop: '12px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <select onChange={(e) => updateStaffRole(member.id, e.target.value)} value={member.role || 'agent'} style={{ padding: '6px', borderRadius: '6px', border: '1px solid #cbd5e1' }}>
                {['agent', 'cro', 'accounting', 'marketing', 'legal', 'recruitment', 'leadership'].map(r => <option key={r} value={r}>{r}</option>)}
              </select>
              <button onClick={() => { setEditingItem(member); setFormData(member); setShowModal(true) }} style={buttonStyle}>✏️ Edit</button>
              <button onClick={() => handleDelete(member.id, 'users')} style={deleteButtonStyle}>🗑️ Delete</button>
              <WhatsAppButton type="staff" data={member} phoneNumber="" session={session} companyName={companyName} buttonText="📱 Contact" />
            </div>
          </div>
        ))}
      </div>
    )
  }

  // ---------- Main list renderer ----------
  const renderList = () => {
    if (loading) return <div style={{ textAlign: 'center', padding: '40px' }}>⏳ Loading...</div>

    const cardStyle = { background: '#fff', borderRadius: '12px', padding: '20px', border: '1px solid #e2e8f0', marginBottom: '16px' }
    const buttonStyle = { background: '#2c7da0', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: '6px', cursor: 'pointer', marginRight: '8px' }
    const deleteButtonStyle = { ...buttonStyle, background: '#dc2626' }

    if (activeTab === 'clients') {
      return (
        <div>
          <div style={{ marginBottom: '20px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <input type="text" placeholder="Search clients..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} style={{ padding: '10px', border: '1px solid #cbd5e1', borderRadius: '8px', flex: 1 }} />
            <button onClick={() => exportToExcel(filteredClients, 'clients')} style={{ ...buttonStyle, background: '#059669' }}>📥 Export to Excel</button>
          </div>
          <div style={{ display: 'grid', gap: '16px' }}>
            {filteredClients.map(client => (
              <div key={client.id} style={cardStyle}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', flexWrap: 'wrap' }}>
                  <div style={{ flex: 1 }}>
                    <h3 style={{ margin: '0 0 8px 0' }}>{client.full_name}</h3>
                    <p style={{ margin: '4px 0' }}>📧 {client.email}</p>
                    <p style={{ margin: '4px 0' }}>📞 {client.phone || 'N/A'}</p>
                    <p style={{ margin: '4px 0' }}>📍 {client.address || 'N/A'}</p>
                    <p style={{ margin: '4px 0' }}>👤 Type: {client.client_type}</p>
                    {client.bio && <p style={{ margin: '4px 0', fontSize: '13px', color: '#64748b' }}>📝 {client.bio.substring(0, 100)}...</p>}
                  </div>
                  <div>
                    <WhatsAppButton type="client" data={client} phoneNumber={client.phone} session={session} companyName={companyName} buttonText="📱 WhatsApp" />
                    <button onClick={() => { setEditingItem(client); setFormData(client); setShowModal(true) }} style={buttonStyle}>✏️ Edit</button>
                    <button onClick={() => handleDelete(client.id, 'clients')} style={deleteButtonStyle}>🗑️ Delete</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )
    }

    if (activeTab === 'properties') {
      const filtered = properties.filter(p =>
        p.address?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.property_type?.toLowerCase().includes(searchTerm.toLowerCase())
      )
      return (
        <div>
          <div style={{ marginBottom: '20px' }}>
            <input type="text" placeholder="Search properties..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} style={{ padding: '10px', border: '1px solid #cbd5e1', borderRadius: '8px', width: '100%' }} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: '20px' }}>
            {filtered.map(property => (
              <div key={property.id} style={cardStyle}>
                <h3>{property.property_type || 'Property'}</h3>
                <p>📍 {property.address}</p>
                <p>💰 Price: ${property.price?.toLocaleString()}</p>
                <p>🛏️ {property.bedrooms} beds | 🚽 {property.bathrooms} baths</p>
                <p>📐 {property.size} sq ft</p>
                <p>👤 Client: {property.client_name}</p>
                <p>Status: <span style={{ background: property.status === 'available' ? '#d1fae5' : '#fee2e2', padding: '4px 8px', borderRadius: '4px' }}>{property.status}</span></p>
                {Array.isArray(property.features) && property.features.length > 0 && (
                  <p style={{ fontSize: '12px' }}>✨ Features: {property.features.join(', ')}</p>
                )}
                <div style={{ marginTop: '12px' }}>
                  <button onClick={() => { setEditingItem(property); setFormData(property); setShowModal(true) }} style={buttonStyle}>✏️ Edit</button>
                  <button onClick={() => handleDelete(property.id, 'properties')} style={deleteButtonStyle}>🗑️ Delete</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )
    }

    if (activeTab === 'staff') return renderStaffList()

    if (activeTab === 'documents') {
      if (documents.length === 0) return <p>No documents. Click "Upload Document" to add one.</p>
      return (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '20px' }}>
          {documents.map(doc => (
            <div key={doc.id} style={cardStyle}>
              <h3>📄 {doc.title}</h3>
              <p>Type: {doc.type || 'General'}</p>
              <p>Version: {doc.version || '1.0'}</p>
              <p>Uploaded: {new Date(doc.created_at).toLocaleDateString()}</p>
              <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
                <a href={doc.file_url} target="_blank" rel="noopener noreferrer" style={{ ...buttonStyle, background: '#059669', textDecoration: 'none' }}>📥 Download</a>
                <button onClick={() => deleteDocument(doc.id)} style={deleteButtonStyle}>🗑️ Delete</button>
              </div>
            </div>
          ))}
        </div>
      )
    }

    if (activeTab === 'interactions') {
      return (
        <div style={{ display: 'grid', gap: '16px' }}>
          {interactions.map(interaction => (
            <div key={interaction.id} style={cardStyle}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', flexWrap: 'wrap' }}>
                <div>
                  <h3 style={{ margin: '0 0 8px 0' }}>{interaction.interaction_type} with {interaction.clients?.full_name}</h3>
                  <p>📅 {interaction.interaction_date ? new Date(interaction.interaction_date).toLocaleString() : 'N/A'}</p>
                  <p>📝 {interaction.notes}</p>
                  {interaction.follow_up_needed === 'yes' && interaction.follow_up_date && (
                    <p style={{ color: '#f59e0b' }}>⏰ Follow-up needed: {new Date(interaction.follow_up_date).toLocaleString()}</p>
                  )}
                </div>
                <div>
                  <button onClick={() => { setEditingItem(interaction); setFormData(interaction); setShowModal(true) }} style={buttonStyle}>✏️ Edit</button>
                  <button onClick={() => handleDelete(interaction.id, 'client_interactions')} style={deleteButtonStyle}>🗑️ Delete</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )
    }

    if (activeTab === 'complaints') {
      return (
        <div style={{ display: 'grid', gap: '16px' }}>
          <div style={{ marginBottom: '20px', display: 'flex', gap: '10px' }}>
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ padding: '10px', border: '1px solid #cbd5e1', borderRadius: '8px' }}>
              <option value="all">All</option>
              <option value="open">Open</option>
              <option value="in_progress">In Progress</option>
              <option value="resolved">Resolved</option>
            </select>
          </div>
          {complaints.filter(c => filterStatus === 'all' || c.status === filterStatus).map(complaint => (
            <div key={complaint.id} style={cardStyle}>
              <div style={{ borderLeft: `4px solid ${getPriorityColor(complaint.priority)}`, paddingLeft: '16px' }}>
                <h3>{complaint.complaint_type} - {complaint.clients?.full_name}</h3>
                <p>⚠️ {complaint.description}</p>
                <p>Status: <span style={{ background: complaint.status === 'resolved' ? '#d1fae5' : '#fee2e2', padding: '4px 8px', borderRadius: '4px' }}>{complaint.status}</span></p>
                <p>Priority: <span style={{ color: getPriorityColor(complaint.priority) }}>{complaint.priority}</span></p>
                {complaint.resolution_notes && <p>✅ Resolution: {complaint.resolution_notes}</p>}
                <div style={{ marginTop: '12px' }}>
                  <button onClick={() => { setEditingItem(complaint); setFormData(complaint); setShowModal(true) }} style={buttonStyle}>✏️ Edit</button>
                  <button onClick={() => handleDelete(complaint.id, 'complaints')} style={deleteButtonStyle}>🗑️ Delete</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )
    }

    if (activeTab === 'feedback') {
      return (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: '20px' }}>
          {feedback.map(item => (
            <div key={item.id} style={cardStyle}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                <span style={{ fontSize: '24px' }}>{'⭐'.repeat(item.rating || 0)}</span>
                <span style={{ color: '#6b7280' }}>({item.rating}/5)</span>
              </div>
              <p><strong>{item.clients?.full_name}</strong> - {item.category}</p>
              <p>"{item.feedback}"</p>
              <p style={{ fontSize: '12px', color: '#6b7280' }}>{new Date(item.created_at).toLocaleDateString()}</p>
              <button onClick={() => handleDelete(item.id, 'feedback')} style={deleteButtonStyle}>🗑️ Delete</button>
            </div>
          ))}
        </div>
      )
    }

    if (activeTab === 'appointments') {
      return (
        <div style={{ display: 'grid', gap: '16px' }}>
          {appointments.map(apt => {
            const status = getStatusBadge(apt.status)
            return (
              <div key={apt.id} style={cardStyle}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', flexWrap: 'wrap' }}>
                  <div>
                    <h3>{apt.appointment_type} with {apt.clients?.full_name}</h3>
                    <p>📅 {apt.appointment_date ? new Date(apt.appointment_date).toLocaleString() : 'N/A'}</p>
                    <p>📍 Location: {apt.location || apt.meeting_link || 'TBD'}</p>
                    <p>📝 {apt.notes}</p>
                    <p>Status: <span style={{ background: status.bg, color: status.color, padding: '4px 12px', borderRadius: '20px', fontSize: '12px' }}>{status.label}</span></p>
                  </div>
                  <div>
                    <button onClick={() => { setEditingItem(apt); setFormData(apt); setShowModal(true) }} style={buttonStyle}>✏️ Edit</button>
                    <button onClick={() => handleDelete(apt.id, 'appointments')} style={deleteButtonStyle}>🗑️ Delete</button>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )
    }

    if (activeTab === 'reports') {
      return (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
          <div style={cardStyle}>
            <h3>📊 Client Statistics</h3>
            <p>Total Clients: <strong>{stats.totalClients}</strong></p>
            <p>Buyers: <strong>{clients.filter(c => c.client_type === 'buyer').length}</strong></p>
            <p>Sellers: <strong>{clients.filter(c => c.client_type === 'seller').length}</strong></p>
            <p>Renters: <strong>{clients.filter(c => c.client_type === 'renter').length}</strong></p>
            <button onClick={() => exportToExcel(clients, 'client_report')} style={buttonStyle}>📥 Export Report</button>
          </div>

          <div style={cardStyle}>
            <h3>🏠 Property Statistics</h3>
            <p>Total Properties: <strong>{stats.totalProperties}</strong></p>
            <p>Available: <strong>{properties.filter(p => p.status === 'available').length}</strong></p>
            <p>Sold: <strong>{properties.filter(p => p.status === 'sold').length}</strong></p>
            <p>Rented: <strong>{properties.filter(p => p.status === 'rented').length}</strong></p>
          </div>

          <div style={cardStyle}>
            <h3>⚠️ Complaint Summary</h3>
            <p>Active Complaints: <strong>{stats.activeComplaints}</strong></p>
            <p>Resolved: <strong>{complaints.filter(c => c.status === 'resolved').length}</strong></p>
            <p>High Priority: <strong>{complaints.filter(c => ['high', 'urgent'].includes(c.priority)).length}</strong></p>
          </div>

          <div style={cardStyle}>
            <h3>⭐ Customer Satisfaction</h3>
            <p>Satisfaction Rate: <strong>{stats.satisfactionRate}%</strong></p>
            <p>Total Feedback: <strong>{feedback.length}</strong></p>
            <p>Avg Rating: <strong>{(feedback.reduce((s, f) => s + f.rating, 0) / feedback.length || 0).toFixed(1)}/5</strong></p>
          </div>

          <div style={cardStyle}>
            <h3>📅 Appointments</h3>
            <p>Total: <strong>{appointments.length}</strong></p>
            <p>Scheduled: <strong>{appointments.filter(a => a.status === 'scheduled').length}</strong></p>
            <p>Completed: <strong>{appointments.filter(a => a.status === 'completed').length}</strong></p>
            <p>Upcoming: <strong>{appointments.filter(a => new Date(a.appointment_date) > new Date() && a.status === 'scheduled').length}</strong></p>
          </div>

          <div style={cardStyle}>
            <h3>👥 Staff Overview</h3>
            <p>Total Staff: <strong>{stats.totalStaff}</strong></p>
            <p>Agents: <strong>{staff.filter(s => s.role === 'agent').length}</strong></p>
            <p>CROs: <strong>{staff.filter(s => s.role === 'cro').length}</strong></p>
            <p>Other Roles: <strong>{staff.filter(s => !['agent', 'cro'].includes(s.role)).length}</strong></p>
          </div>

          <div style={cardStyle}>
            <h3>📄 Documents</h3>
            <p>Total Documents: <strong>{stats.totalDocuments}</strong></p>
            <button onClick={() => exportToExcel(documents, 'documents_report')} style={buttonStyle}>📥 Export Document List</button>
          </div>
        </div>
      )
    }

    if (activeTab === 'whatsapp') {
      return (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: '20px' }}>
          <div style={cardStyle}>
            <h3>📱 Quick WhatsApp Actions</h3>
            <p>Send updates to clients directly</p>
            {clients.slice(0, 5).map(client => (
              <div key={client.id} style={{ marginTop: '12px', padding: '12px', background: '#f8fafc', borderRadius: '8px' }}>
                <p><strong>{client.full_name}</strong> - {client.phone}</p>
                <WhatsAppButton type="client" data={client} phoneNumber={client.phone} session={session} companyName={companyName} buttonText="📱 Send Message" />
              </div>
            ))}
          </div>

          <div style={cardStyle}>
            <h3>📢 Broadcast Templates</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <WhatsAppButton type="bulk" data={{ message: `🏠 New properties available at ${companyName}! Check out our latest listings.` }} phoneNumber="" session={session} companyName={companyName} buttonText="🏠 Property Update" />
              <WhatsAppButton type="bulk" data={{ message: `📅 Reminder: You have an upcoming appointment with ${companyName}. Please confirm your attendance.` }} phoneNumber="" session={session} companyName={companyName} buttonText="📅 Appointment Reminder" />
              <WhatsAppButton type="bulk" data={{ message: `⭐ We value your feedback! Please rate your experience with ${companyName}.` }} phoneNumber="" session={session} companyName={companyName} buttonText="⭐ Feedback Request" />
            </div>
          </div>
        </div>
      )
    }

    return null
  }

  // ---------- Styles ----------
  const headerStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px', flexWrap: 'wrap', gap: '15px' }
  const addBtn = { background: '#1e3a5f', color: '#fff', border: 'none', padding: '10px 24px', borderRadius: '25px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }
  const modalOverlay = { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }
  const modalStyle = { background: '#fff', padding: '25px', borderRadius: '20px', width: '90%', maxWidth: '550px', maxHeight: '80vh', overflow: 'auto' }
  const cancelBtn = { background: '#6b7280', color: '#fff', border: 'none', padding: '10px 20px', borderRadius: '8px', cursor: 'pointer' }
  const saveBtn = { background: '#059669', color: '#fff', border: 'none', padding: '10px 20px', borderRadius: '8px', cursor: 'pointer', fontWeight: 600 }
  const modalOverlayStyle = { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000, padding: '20px' }
  const inputStyle = { width: '100%', padding: '10px', margin: '6px 0', border: '1px solid #cbd5e1', borderRadius: '8px', boxSizing: 'border-box' }

  if (loading && !companyId) {
    return <div style={{ textAlign: 'center', padding: '50px' }}>Loading company data...</div>
  }

  return (
    <PortalLayout
      session={session}
      onLogout={onLogout}
      activeTab={activeTab}
      setActiveTab={setActiveTab}
      tabs={tabs}
      unreadCount={unreadCount}
      portalTitle="Customer Relations Portal"
      portalIcon="👥"
      onShowMessages={() => setShowMessages(true)}
    >
      {showMessages && (
        <div style={modalOverlayStyle} onClick={() => setShowMessages(false)}>
          <div onClick={e => e.stopPropagation()}>
            <MessagingSystem
              currentUser={session.user}
              userRole="cro"
              onClose={() => setShowMessages(false)}
            />
          </div>
        </div>
      )}

      <div style={headerStyle}>
        <div>
          <h1 style={{ margin: 0 }}>{tabs.find(t => t.id === activeTab)?.label || 'Dashboard'}</h1>
          <p style={{ color: '#64748b', marginTop: '5px' }}>Welcome, {session.user.email} - {companyName}</p>
        </div>
        {!['dashboard', 'reports', 'whatsapp', 'documents'].includes(activeTab) && (
          <button onClick={() => { setEditingItem(null); setFormData({}); setShowModal(true) }} style={addBtn}>+ Add New</button>
        )}
        {activeTab === 'documents' && (
          <button onClick={() => { setDocFormData({}); setDocFile(null); setShowDocModal(true) }} style={addBtn}>📎 Upload Document</button>
        )}
      </div>

      {errorMsg && <div style={{ background: '#fee2e2', color: '#dc2626', padding: '10px', borderRadius: '8px', marginBottom: '20px' }}>⚠️ {errorMsg}</div>}

      {activeTab === 'dashboard' && (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '20px', marginBottom: '30px' }}>
            {[
              { title: '👥 Total Clients', value: stats.totalClients },
              { title: '🏠 Properties', value: stats.totalProperties },
              { title: '⚠️ Active Complaints', value: stats.activeComplaints, color: '#dc2626' },
              { title: '📅 Pending Appointments', value: stats.pendingAppointments },
              { title: '⭐ Satisfaction', value: `${stats.satisfactionRate}%` }
            ].map((card, i) => (
              <div key={i} style={{ background: '#fff', padding: '20px', borderRadius: '16px', textAlign: 'center', border: '1px solid #e2e8f0' }}>
                <h3>{card.title}</h3>
                <p style={{ fontSize: '32px', fontWeight: 'bold', color: card.color || '#1e3a5f', marginTop: '10px' }}>{card.value}</p>
              </div>
            ))}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '20px' }}>
            <div style={{ background: '#fff', borderRadius: '16px', padding: '20px', border: '1px solid #e2e8f0' }}>
              <h3>Recent Interactions</h3>
              {interactions.slice(0, 5).map(i => (
                <div key={i.id} style={{ padding: '10px 0', borderBottom: '1px solid #e2e8f0' }}>
                  <p><strong>{i.interaction_type}</strong> with {i.clients?.full_name}</p>
                  <p style={{ fontSize: '12px', color: '#6b7280' }}>{i.interaction_date ? new Date(i.interaction_date).toLocaleString() : ''}</p>
                </div>
              ))}
            </div>

            <div style={{ background: '#fff', borderRadius: '16px', padding: '20px', border: '1px solid #e2e8f0' }}>
              <h3>Recent Feedback</h3>
              {feedback.slice(0, 5).map(f => (
                <div key={f.id} style={{ padding: '10px 0', borderBottom: '1px solid #e2e8f0' }}>
                  <p><strong>{f.clients?.full_name}</strong> - {'⭐'.repeat(f.rating || 0)}</p>
                  <p style={{ fontSize: '14px' }}>"{String(f.feedback || '').substring(0, 100)}..."</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <div style={{ marginTop: '20px' }}>{renderList()}</div>

      {showModal && (
        <div style={modalOverlay} onClick={() => setShowModal(false)}>
          <div style={modalStyle} onClick={e => e.stopPropagation()}>
            <h2>{editingItem ? '✏️ Edit' : '➕ Add New'}</h2>
            <form onSubmit={handleSubmit}>
              {renderForm()}
              <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                <button type="submit" style={saveBtn}>Save</button>
                <button type="button" onClick={() => setShowModal(false)} style={cancelBtn}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showDocModal && (
        <div style={modalOverlay} onClick={() => setShowDocModal(false)}>
          <div style={{ ...modalStyle, maxWidth: '450px' }} onClick={e => e.stopPropagation()}>
            <h2>📎 Upload Document</h2>
            <form onSubmit={handleDocumentUpload}>
              <input
                placeholder="Document Title"
                value={docFormData.title || ''}
                onChange={e => setDocFormData({ ...docFormData, title: e.target.value })}
                style={inputStyle}
              />
              <select
                value={docFormData.type || 'general'}
                onChange={e => setDocFormData({ ...docFormData, type: e.target.value })}
                style={inputStyle}
              >
                {documentTypes.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
              <select
                value={docFormData.client_id || ''}
                onChange={e => setDocFormData({ ...docFormData, client_id: e.target.value })}
                style={inputStyle}
              >
                <option value="">Select Client (optional)</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.full_name}</option>)}
              </select>
              <input
                placeholder="Version (e.g. 1.0)"
                value={docFormData.version || ''}
                onChange={e => setDocFormData({ ...docFormData, version: e.target.value })}
                style={inputStyle}
              />
              <textarea
                placeholder="Description"
                rows="2"
                value={docFormData.description || ''}
                onChange={e => setDocFormData({ ...docFormData, description: e.target.value })}
                style={inputStyle}
              />
              <input
                type="file"
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                onChange={e => setDocFile(e.target.files[0])}
                style={inputStyle}
              />
              {uploading && <p>Uploading...</p>}
              <div style={{ display: 'flex', gap: '10px', marginTop: '15px' }}>
                <button type="submit" disabled={uploading} style={saveBtn}>{uploading ? 'Uploading...' : 'Upload'}</button>
                <button type="button" onClick={() => setShowDocModal(false)} style={cancelBtn}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </PortalLayout>
  )
}

export default CROPortal